"use client";

import { useServices } from "../services-context";
import { useCallback, useMemo, useRef } from "react";
import { usePatientVerification } from "@/hooks/self-schedule/use-patient-verification";
import { cx } from "@/lib/tailwind-utils";
import { ServiceVisibilityType } from "@/types/enums/service";
import { PublicServiceDetail } from "@/types/service";
import { EmptyServicesState } from "./empty-services-state";
import { FlatServicesMenu } from "./flat-services-menu";
import { filterServices, sortServicesForSearch } from "./helpers";
import { PriceDisclaimer } from "./price-disclaimer";
import { PROMOTION_CATEGORY_NAME, hasRealPromotionCategory, isPromotionCategoryName, isPromotionService } from "./promotion-category";
import { createSelectionHandler } from "./selection";
import { ServiceMenuItem } from "./service-menu-item";
import { TaggedServiceAccordion } from "./tagged-service-accordion";
import { TaggedServicesCategoryButton, TaggedServicesLayout } from "./tagged-services-layout";
import { getLargeLayoutCategoryName, getSidebarCategoryName, groupServicesByCategory, groupServicesByTag } from "./tagged-services-utils";
import { useServicesView } from "./use-services-view";
import { FIRST_AVAILABLE_PROVIDER_ID } from "@/stores/self-schedule-store";
export const TaggedServicesView = ({
  className,
  onOpenDetails,
  onRequireMfa,
  onPhoneOnlyService,
  onOpenSelectionsDrawer
}: {
  className?: string;
  onOpenDetails?: (serviceId: string) => void;
  onRequireMfa?: (serviceId: string, onSelect?: (serviceId: string) => void) => void;
  onPhoneOnlyService?: (serviceId: string) => void;
  onOpenSelectionsDrawer?: (serviceId: string) => void;
}) => {
  const {
    state: {
      data,
      searchQuery,
      selectedCategory,
      locationType,
      selectedLocationId,
      selectedLocation,
      patientType,
      selectedProviderId,
      selectedServiceIds,
      effectiveNumAllowedServices
    },
    actions: {
      addService,
      removeService,
      removeAllServiceInstances,
      getServiceInstanceCount,
      setSelectedCategory: setSelectedCategoryAction
    }
  } = useServices();
  const {
    frontPageData,
    categories,
    isLoading,
    isSearching,
    isServiceSelected,
    isServiceUnavailable,
    canSelectMore,
    handleInfoClick,
    useCategoryHierarchy
  } = useServicesView({
    onOpenDetails,
    onRequireMfa,
    onPhoneOnlyService,
    onOpenSelectionsDrawer
  });
  const ref = useRef<HTMLDivElement>(null);
  const {
    blockingAppointment
  } = usePatientVerification();
  const providerFilterId = selectedProviderId === FIRST_AVAILABLE_PROVIDER_ID ? null : selectedProviderId;
  const searchFilteredServices = useMemo(() => {
    const baseServices = filterServices(frontPageData?.services, {
      searchQuery,
      selectedCategory: undefined,
      locations: frontPageData?.locations,
      locationType,
      selectedLocationId: selectedLocation?.id ?? selectedLocationId,
      selectedProviderId: providerFilterId,
      patientType,
      categories
    });
    return sortServicesForSearch(baseServices, searchQuery);
  }, [frontPageData?.services, frontPageData?.locations, locationType, selectedLocation?.id, selectedLocationId, providerFilterId, patientType, categories, searchQuery]);
  const setSelectedCategory = useCallback((category: string) => {
    if (category === selectedCategory) return;
    setSelectedCategoryAction(category);
    requestAnimationFrame(() => {
      ref.current?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    });
  }, [selectedCategory, setSelectedCategoryAction]);
  const availableCategories = useMemo(() => {
    const useSyntheticPromotionCategory = searchFilteredServices.some(isPromotionService) && !hasRealPromotionCategory(data?.categories);
    const categoryNames = Array.from(new Set(searchFilteredServices.filter(service => useSyntheticPromotionCategory ? !isPromotionService(service) : true).map(service_0 => useCategoryHierarchy ? getSidebarCategoryName(service_0, data?.categories ?? []) : getLargeLayoutCategoryName(service_0))));
    if (useSyntheticPromotionCategory) {
      categoryNames.unshift(PROMOTION_CATEGORY_NAME);
    }
    const categoryByName = new Map((data?.categories ?? []).map(category_0 => [category_0.name, category_0]));

    // Compute indentation only through categories that are actually rendered.
    // Missing parents and cyclic hierarchies stay at depth 0.
    const availableNames = new Set(categoryNames);
    const depthOf = (name: string): number => {
      let depth = 0;
      let parentName = categoryByName.get(name)?.parentCategoryName;
      const seen = new Set([name]);
      while (parentName && availableNames.has(parentName)) {
        if (seen.has(parentName)) return 0;
        seen.add(parentName);
        depth += 1;
        parentName = categoryByName.get(parentName)?.parentCategoryName;
      }
      return depth;
    };
    const enriched = categoryNames.map(name_0 => ({
      name: name_0,
      displayOrder: categoryByName.get(name_0)?.displayOrder,
      depth: depthOf(name_0)
    }));

    // Keep only a synthetic promotion entry first. A real Promotions category
    // must retain the position configured by staff like every other category.
    const promotion = useSyntheticPromotionCategory ? enriched.filter(category_1 => isPromotionCategoryName(category_1.name)) : [];
    const ordered = (useSyntheticPromotionCategory ? enriched.filter(category_2 => !isPromotionCategoryName(category_2.name)) : enriched).sort((a, b) => {
      const aOrder = a.displayOrder ?? Infinity;
      const bOrder = b.displayOrder ?? Infinity;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.name.localeCompare(b.name, undefined, {
        sensitivity: "base"
      });
    });
    return [...promotion, ...ordered];
  }, [data?.categories, searchFilteredServices, useCategoryHierarchy]);
  const activeCategory = useMemo(() => {
    if (availableCategories.some(category_3 => category_3.name === selectedCategory)) {
      return selectedCategory;
    }
    return availableCategories[0]?.name;
  }, [availableCategories, selectedCategory]);
  const activeServices = useMemo(() => {
    if (!activeCategory) return [];
    const useSyntheticPromotionCategory_0 = !hasRealPromotionCategory(data?.categories) && isPromotionCategoryName(activeCategory);
    if (useSyntheticPromotionCategory_0) {
      return searchFilteredServices.filter(isPromotionService);
    }
    return searchFilteredServices.filter(service_1 => !(useCategoryHierarchy && !hasRealPromotionCategory(data?.categories) && isPromotionService(service_1)) && (useCategoryHierarchy ? getSidebarCategoryName(service_1, data?.categories ?? []) : getLargeLayoutCategoryName(service_1)) === activeCategory);
  }, [activeCategory, data?.categories, searchFilteredServices, useCategoryHierarchy]);
  const {
    tagGroups,
    untaggedServices
  } = useMemo(() => useCategoryHierarchy ? groupServicesByCategory(activeServices, activeCategory, data?.categories ?? []) : groupServicesByTag(activeServices), [activeServices, activeCategory, data?.categories, useCategoryHierarchy]);
  const handleServiceClick = useMemo(() => createSelectionHandler(activeServices, {
    addSelectedService: addService,
    removeAllSelectedServiceInstances: removeAllServiceInstances,
    selectedServiceIds,
    numAllowedServices: effectiveNumAllowedServices,
    onRequireMfa,
    onPhoneOnlyService,
    locationType,
    onOpenSelectionsDrawer
  }), [activeServices, addService, removeAllServiceInstances, selectedServiceIds, effectiveNumAllowedServices, locationType, onRequireMfa, onPhoneOnlyService, onOpenSelectionsDrawer]);
  const handleQuantityChange = useCallback((serviceId: string, quantity: number) => {
    const current = getServiceInstanceCount(serviceId);
    const delta = quantity - current;
    if (delta > 0) {
      const service_2 = activeServices.find(({
        id
      }) => id === serviceId);
      const requiresConfirmation = Boolean(service_2?.onlyBookableOverThePhone && onPhoneOnlyService || service_2?.visibility === ServiceVisibilityType.EXISTING && onRequireMfa || (service_2?.addons?.length || 0) > 0 && onOpenSelectionsDrawer);
      const increments = requiresConfirmation ? 1 : delta;
      for (let i = 0; i < increments; i += 1) {
        handleServiceClick(serviceId, "increment");
      }
      return;
    }
    for (let i_0 = 0; i_0 < Math.abs(delta); i_0 += 1) {
      removeService(serviceId);
    }
  }, [activeServices, getServiceInstanceCount, handleServiceClick, onOpenSelectionsDrawer, onPhoneOnlyService, onRequireMfa, removeService]);
  const renderServiceItem = (service_3: PublicServiceDetail, index: number, groupId: string, standalone: boolean, animationDelay = index * 30) => {
    const isSelected = isServiceSelected(service_3.id);
    const selectionCount = getServiceInstanceCount(service_3.id);
    const remaining = Math.max(0, effectiveNumAllowedServices - selectedServiceIds.length);
    const maxQuantity = service_3.allowMultipleBookings ? selectionCount + remaining : selectionCount;
    const disabled = !isSelected && (!!blockingAppointment || !service_3.onlyBookableOverThePhone && (!canSelectMore || isServiceUnavailable(service_3.id)));
    return <ServiceMenuItem key={`${service_3.id}-${groupId}`} standalone={standalone} service={service_3} isSelected={isSelected} selectionCount={selectionCount} disabled={disabled} maxQuantity={maxQuantity} onServiceClick={handleServiceClick} onQuantityChange={handleQuantityChange} onInfoClick={handleInfoClick} showDuration={frontPageData?.showServiceDuration !== false} animationDelay={animationDelay} />;
  };
  if (!frontPageData || isLoading) return null;
  const hasEstimatedPrices = searchFilteredServices.some(service_4 => service_4.estimated);
  if (isSearching) {
    return <FlatServicesMenu services={activeServices} className={className} renderService={(service_5, index_0, animationDelay_0) => renderServiceItem(service_5, index_0, "", true, animationDelay_0)} />;
  }
  return <div className={cx("mt-6", className)}>
      <TaggedServicesLayout sidebar={availableCategories.map(category_4 => {
      const isPromotionCategory = isPromotionCategoryName(category_4.name) && !hasRealPromotionCategory(data?.categories);
      return <TaggedServicesCategoryButton key={category_4.name} title={category_4.name} isActive={category_4.name === activeCategory} onClick={() => setSelectedCategory(category_4.name)} className={isPromotionCategory ? "border-primary" : undefined} depth={category_4.depth ?? 0} />;
    })}>
        <div ref={ref}>
          {activeServices.length === 0 ? <EmptyServicesState /> : <div className="space-y-4">
              {tagGroups.length > 0 && <TaggedServiceAccordion groups={tagGroups} renderService={renderServiceItem} />}
              {untaggedServices.length > 0 && <div className="group/list border rounded-xl shadow-sm">
                  {untaggedServices.map((service_6, index_1) => renderServiceItem(service_6, index_1, "untagged", tagGroups.length === 0 || untaggedServices.length === 1))}
                </div>}
            </div>}
        </div>
      </TaggedServicesLayout>

      {hasEstimatedPrices && <PriceDisclaimer />}
    </div>;
};
export default TaggedServicesView;